import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../services/api';
import './AdminDataManagement.css';

// Pokemon constants for dropdowns
const POKEMON_NATURES = [
  'Adamant', 'Bold', 'Brave', 'Calm', 'Careful', 'Hardy', 'Hasty', 'Impish',
  'Jolly', 'Lax', 'Lonely', 'Mild', 'Modest', 'Naive', 'Naughty', 'Quiet',
  'Quirky', 'Rash', 'Relaxed', 'Sassy', 'Serious', 'Timid', 'Docile', 'Bashful'
];

// Neutral natures that don't affect stats
const NEUTRAL_NATURES = ['Hardy', 'Docile', 'Serious', 'Bashful', 'Quirky'];

// Helper function to display nature names
const formatNatureName = (nature: string): string => {
  return NEUTRAL_NATURES.includes(nature) ? 'Neutral' : nature;
};

const HIDDEN_POWER_TYPES = [
  'Fighting', 'Flying', 'Poison', 'Ground', 'Rock', 'Bug', 'Ghost', 'Steel',
  'Fire', 'Water', 'Grass', 'Electric', 'Psychic', 'Ice', 'Dragon', 'Dark'
];

interface PokemonEntry {
  sale_id: number;
  pokemon_id: number;
  pokemon_name: string;
  form: string;
  price: number;
  sale_date: string;
  submitted_by: string;
  approved_by: string;
  created_at: string;
  updated_at: string;
  status?: string;
  proof_url?: string;
  // Pokemon stats
  nature?: string;
  ability?: string;
  hp_iv?: number;
  atk_iv?: number;
  def_iv?: number;
  spatk_iv?: number;
  spdef_iv?: number;
  speed_iv?: number;
  hidden_power?: string;
}

interface ItemEntry {
  sale_id: number;
  item_name: string;
  price: number;
  sale_date: string;
  submitted_by: string;
  approved_by: string;
  created_at: string;
  updated_at: string;
  status?: string;
  proof_url?: string;
}

interface SpriteEntry {
  id: number;
  pokemon_id: number;
  pokemon_name: string;
  variant: string;
  imgur_url: string;
  created_at: string;
  updated_at: string;
}

interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

type TabType = 'pokemon' | 'items' | 'sprites';

const AdminDataManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('pokemon');
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Pokemon state
  const [pokemonData, setPokemonData] = useState<PokemonEntry[]>([]);
  const [pokemonPagination, setPokemonPagination] = useState<PaginationInfo | null>(null);

  // Items state
  const [itemsData, setItemsData] = useState<ItemEntry[]>([]);
  const [itemsPagination, setItemsPagination] = useState<PaginationInfo | null>(null);

  // Sprites state
  const [spritesData, setSpritesData] = useState<SpriteEntry[]>([]);
  const [spritesPagination, setSpritesPagination] = useState<PaginationInfo | null>(null);

  // Edit modal state
  const [editingItem, setEditingItem] = useState<any>(null);
  const [editFormData, setEditFormData] = useState<any>({});

  // Sprite creation modal state
  const [showCreateSprite, setShowCreateSprite] = useState(false);
  const [newSpriteData, setNewSpriteData] = useState({
    pokemon_id: '',
    pokemon_name: '',
    variant: '',
    imgur_url: ''
  });

  // Users for dropdowns
  const [users, setUsers] = useState<{ user_id: number; username: string }[]>([]);

  useEffect(() => {
    loadData();
    loadUsers();
  }, [activeTab, searchTerm]);

  const loadUsers = async () => {
    try {
      const users = await adminAPI.getUsers();
      setUsers(users);
    } catch (error) {
      console.error('Failed to load users:', error);
    }
  };

  const loadData = async (page: number = 1) => {
    setLoading(true);
    try {
      const params = {
        page,
        limit: 50,
        search: searchTerm
      };

      if (activeTab === 'pokemon') {
        const response = await adminAPI.getAllPokemon(params);
        setPokemonData(response.data);
        setPokemonPagination(response.pagination);
      } else if (activeTab === 'items') {
        const response = await adminAPI.getAllItems(params);
        setItemsData(response.data);
        setItemsPagination(response.pagination);
      } else if (activeTab === 'sprites') {
        const response = await adminAPI.getAllSprites(params);
        setSpritesData(response.data);
        setSpritesPagination(response.pagination);
      }
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (item: any) => {
    setEditingItem(item);
    setEditFormData({ ...item });
  };

  const handleSaveEdit = async () => {
    if (!editingItem) return;

    try {
      if (activeTab === 'pokemon') {
        await adminAPI.updatePokemon(editingItem.sale_id, editFormData);
      } else if (activeTab === 'items') {
        await adminAPI.updateItem(editingItem.sale_id, editFormData);
      } else if (activeTab === 'sprites') {
        await adminAPI.updateSprite(editingItem.id, editFormData);
      }

      setEditingItem(null);
      setEditFormData({});
      loadData();
    } catch (error) {
      console.error('Failed to save changes:', error);
      alert('Failed to save changes. Please try again.');
    }
  };

  const handleDeleteSprite = async (id: number) => {
    // Use window.confirm to explicitly reference the global confirm
    if (!window.confirm('Are you sure you want to delete this sprite?')) return;

    try {
      await adminAPI.deleteSprite(id);
      loadData();
    } catch (error) {
      console.error('Failed to delete sprite:', error);
      alert('Failed to delete sprite. Please try again.');
    }
  };

  const handleCreateSprite = async () => {
    try {
      await adminAPI.createSprite(newSpriteData);
      setShowCreateSprite(false);
      setNewSpriteData({
        pokemon_id: '',
        pokemon_name: '',
        variant: '',
        imgur_url: ''
      });
      loadData();
    } catch (error) {
      console.error('Failed to create sprite:', error);
      alert('Failed to create sprite. Please try again.');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const formatDateForInput = (dateString: string) => {
    const date = new Date(dateString);
    return date.toISOString().split('T')[0]; // Returns YYYY-MM-DD format
  };

  const formatPriceWithCommas = (price: number | string) => {
    const num = typeof price === 'string' ? parseFloat(price) : price;
    return Math.round(num).toLocaleString();
  };

  const parsePriceInput = (value: string): string => {
    if (!value) return '';
    // Remove commas and return the numeric string
    return value.replace(/,/g, '');
  };

  const renderPokemonEditForm = () => (
    <div className="edit-form">
      <div className="form-group">
        <label>Status</label>
        <select
          value={editFormData.status || 'pending'}
          onChange={e => setEditFormData({ ...editFormData, status: e.target.value })}
        >
          <option value="approved">Approved</option>
          <option value="pending">Pending</option>
          <option value="rejected">Rejected</option>
        </select>
      </div>
      <div className="form-group">
        <label>Pokemon Name</label>
        <input
          type="text"
          value={editFormData.pokemon_name || ''}
          onChange={(e) => setEditFormData({
            ...editFormData,
            pokemon_name: e.target.value
          })}
        />
      </div>
      <div className="form-group">
        <label>Form</label>
        <input
          type="text"
          value={editFormData.form || ''}
          onChange={(e) => setEditFormData({
            ...editFormData,
            form: e.target.value
          })}
        />
      </div>
      <div className="form-group">
        <label>Price</label>
        <input
          type="text"
          value={formatPriceWithCommas(editFormData.price || 0)}
          onChange={(e) => setEditFormData({
            ...editFormData,
            price: parsePriceInput(e.target.value)
          })}
        />
      </div>
      <div className="form-group">
        <label>Sale Date</label>
        <input
          type="date"
          value={editFormData.sale_date ? formatDateForInput(editFormData.sale_date) : ''}
          onChange={(e) => setEditFormData({
            ...editFormData,
            sale_date: e.target.value
          })}
        />
      </div>
      <div className="form-group">
        <label>Nature</label>
        <select
          value={editFormData.nature || ''}
          onChange={(e) => setEditFormData({
            ...editFormData,
            nature: e.target.value
          })}
        >
          <option value="">Select Nature</option>
          {POKEMON_NATURES.map(nature => (
            <option key={nature} value={nature}>{formatNatureName(nature)}</option>
          ))}
        </select>
      </div>
      <div className="form-group">
        <label>Ability</label>
        <input
          type="text"
          value={editFormData.ability || ''}
          onChange={(e) => setEditFormData({
            ...editFormData,
            ability: e.target.value
          })}
          placeholder="Enter ability name"
        />
      </div>
      <div className="form-group">
        <label>Hidden Power</label>
        <select
          value={editFormData.hidden_power || ''}
          onChange={(e) => setEditFormData({
            ...editFormData,
            hidden_power: e.target.value
          })}
        >
          <option value="">Select Hidden Power</option>
          {HIDDEN_POWER_TYPES.map(type => (
            <option key={type} value={type}>{type}</option>
          ))}
        </select>
      </div>
      {/* IV Fields */}
      <div className="form-group">
        <label>HP IV</label>
        <input
          type="number"
          min="0"
          max="31"
          value={editFormData.hp_iv || ''}
          onChange={(e) => setEditFormData({
            ...editFormData,
            hp_iv: e.target.value
          })}
        />
      </div>
      <div className="form-group">
        <label>Attack IV</label>
        <input
          type="number"
          min="0"
          max="31"
          value={editFormData.atk_iv || ''}
          onChange={(e) => setEditFormData({
            ...editFormData,
            atk_iv: e.target.value
          })}
        />
      </div>
      <div className="form-group">
        <label>Defense IV</label>
        <input
          type="number"
          min="0"
          max="31"
          value={editFormData.def_iv || ''}
          onChange={(e) => setEditFormData({
            ...editFormData,
            def_iv: e.target.value
          })}
        />
      </div>
      <div className="form-group">
        <label>Special Attack IV</label>
        <input
          type="number"
          min="0"
          max="31"
          value={editFormData.spatk_iv || ''}
          onChange={(e) => setEditFormData({
            ...editFormData,
            spatk_iv: e.target.value
          })}
        />
      </div>
      <div className="form-group">
        <label>Special Defense IV</label>
        <input
          type="number"
          min="0"
          max="31"
          value={editFormData.spdef_iv || ''}
          onChange={(e) => setEditFormData({
            ...editFormData,
            spdef_iv: e.target.value
          })}
        />
      </div>
      <div className="form-group">
        <label>Speed IV</label>
        <input
          type="number"
          min="0"
          max="31"
          value={editFormData.speed_iv || ''}
          onChange={(e) => setEditFormData({
            ...editFormData,
            speed_iv: e.target.value
          })}
        />
      </div>
      <div className="form-group">
        <label>Submitted By</label>
        <select
          value={editFormData.submitted_by ?? ''}
          onChange={(e) => setEditFormData({
            ...editFormData,
            submitted_by: e.target.value === '' ? null : Number(e.target.value)
          })}
        >
          <option value="">-- None --</option>
          {users.map(user => (
            <option key={user.user_id} value={user.user_id}>{user.username}</option>
          ))}
        </select>
      </div>
      <div className="form-group">
        <label>Approved By</label>
        <select
          value={editFormData.approved_by ?? ''}
          onChange={(e) => setEditFormData({
            ...editFormData,
            approved_by: e.target.value === '' ? null : Number(e.target.value)
          })}
        >
          <option value="">-- None --</option>
          {users.map(user => (
            <option key={user.user_id} value={user.user_id}>{user.username}</option>
          ))}
        </select>
      </div>
    </div>
  );

  const renderGenericEditForm = () => (
    <div className="edit-form">
      {Object.keys(editFormData).map((key) => {
        if (key.includes('_at') || key === 'sale_id' || key === 'id') return null;

        // Special handling for user dropdowns
        if (key === 'submitted_by' || key === 'approved_by') {
          return (
            <div key={key} className="form-group">
              <label>{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</label>
              <select
                value={editFormData[key] ?? ''}
                onChange={(e) => setEditFormData({
                  ...editFormData,
                  [key]: e.target.value === '' ? null : Number(e.target.value)
                })}
              >
                <option value="">-- None --</option>
                {users.map(user => (
                  <option key={user.user_id} value={user.user_id}>{user.username}</option>
                ))}
              </select>
            </div>
          );
        }

        if (key === 'status') {
          return (
            <div key={key} className="form-group">
              <label>Status</label>
              <select
                value={editFormData.status || ''}
                onChange={(e) => setEditFormData({
                  ...editFormData,
                  status: e.target.value
                })}
              >
                <option value="">Select Status</option>
                <option value="approved">Approved</option>
                <option value="pending">Pending</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
          );
        }
        if (key === 'status') {
          return (
            <div key={key} className="form-group">
              <label>Status</label>
              <select
                value={editFormData.status || ''}
                onChange={e => setEditFormData({ ...editFormData, status: e.target.value })}
              >
                <option value="">Select Status</option>
                <option value="approved">Approved</option>
                <option value="pending">Pending</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
          );
        }
        return (
          <div key={key} className="form-group">
            <label>{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</label>
            <input
              type={key.includes('price') || key.includes('quantity') || key.includes('id') ? 'number' : key.includes('date') ? 'date' : 'text'}
              value={key.includes('price') && editFormData[key]
                ? formatPriceWithCommas(editFormData[key])
                : key.includes('date') && editFormData[key]
                ? formatDateForInput(editFormData[key])
                : editFormData[key] || ''}
              onChange={(e) => setEditFormData({
                ...editFormData,
                [key]: key.includes('price') ? parsePriceInput(e.target.value) : e.target.value
              })}
            />
          </div>
        );
      })}
    </div>
  );

  const getCurrentPagination = () => {
    if (activeTab === 'pokemon') return pokemonPagination;
    if (activeTab === 'items') return itemsPagination;
    return spritesPagination;
  };

  const getCurrentData = () => {
    if (activeTab === 'pokemon') return pokemonData;
    if (activeTab === 'items') return itemsData;
    return spritesData;
  };

  const renderPaginationControls = () => {
    const pagination = getCurrentPagination();
    if (!pagination) return null;

    return (
      <div className="pagination-controls">
        <button
          onClick={() => loadData(pagination.page - 1)}
          disabled={pagination.page <= 1}
          className="pagination-button"
        >
          ← Previous
        </button>
        <span className="pagination-info">
          Page {pagination.page} of {pagination.totalPages} ({pagination.total} total)
        </span>
        <button
          onClick={() => loadData(pagination.page + 1)}
          disabled={pagination.page >= pagination.totalPages}
          className="pagination-button"
        >
          Next →
        </button>
      </div>
    );
  };

  const renderPokemonTable = () => (
    <div className="data-table-container">
      <table className="data-table">
        <thead>
          <tr>
            <th>ID</th>
            <th>Pokemon</th>
            <th>Form</th>
            <th>Price</th>
            <th>Date</th>
            <th>Status</th>
            <th>Proof Link</th>
            <th>Submitted By</th>
            <th>Approved By</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {pokemonData.map((pokemon) => (
            <tr key={pokemon.sale_id}>
              <td>{pokemon.sale_id}</td>
              <td>{pokemon.pokemon_name}</td>
              <td>{pokemon.form || '-'}</td>
              <td>${Math.round(pokemon.price).toLocaleString()}</td>
              <td>{formatDate(pokemon.sale_date)}</td>
              <td>{pokemon.status ? pokemon.status.charAt(0).toUpperCase() + pokemon.status.slice(1) : '-'}</td>
              <td>{pokemon.proof_url ? (
                <a href={pokemon.proof_url} target="_blank" rel="noopener noreferrer">Proof</a>
              ) : '-'}</td>
              <td>{pokemon.submitted_by || '-'}</td>
              <td>{pokemon.approved_by || '-'}</td>
              <td>
                <button
                  className="edit-button"
                  onClick={() => handleEdit(pokemon)}
                >
                  ✏️ Edit
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const renderItemsTable = () => (
    <div className="data-table-container">
      <table className="data-table">
        <thead>
          <tr>
            <th>ID</th>
            <th>Item Name</th>
            <th>Price</th>
            <th>Date</th>
            <th>Status</th>
            <th>Proof Link</th>
            <th>Submitted By</th>
            <th>Approved By</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {itemsData.map((item) => (
            <tr key={item.sale_id}>
              <td>{item.sale_id}</td>
              <td>{item.item_name}</td>
              <td>${Math.round(item.price).toLocaleString()}</td>
              <td>{formatDate(item.sale_date)}</td>
              <td>{item.status ? item.status.charAt(0).toUpperCase() + item.status.slice(1) : '-'}</td>
              <td>{item.proof_url ? (
                <a href={item.proof_url} target="_blank" rel="noopener noreferrer">Proof</a>
              ) : '-'}</td>
              <td>{item.submitted_by || '-'}</td>
              <td>{item.approved_by || '-'}</td>
              <td>
                <button
                  className="edit-button"
                  onClick={() => handleEdit(item)}
                >
                  ✏️ Edit
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const renderSpritesTable = () => (
    <div className="data-table-container">
      <div className="sprites-header">
        <button
          className="create-sprite-button"
          onClick={() => setShowCreateSprite(true)}
        >
          ➕ Add New Sprite
        </button>
      </div>
      <table className="data-table">
        <thead>
          <tr>
            <th>ID</th>
            <th>Pokemon ID</th>
            <th>Pokemon Name</th>
            <th>Variant</th>
            <th>Image</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {spritesData.map((sprite) => (
            <tr key={sprite.id}>
              <td>{sprite.id}</td>
              <td>{sprite.pokemon_id}</td>
              <td>{sprite.pokemon_name}</td>
              <td>{sprite.variant}</td>
              <td>
                <img
                  src={sprite.imgur_url.includes('i.imgur.com')
                    ? sprite.imgur_url
                    : `https://i.imgur.com/${sprite.imgur_url.split('/').pop()}.jpg`}
                  alt={`${sprite.pokemon_name} ${sprite.variant}`}
                  className="sprite-preview"
                  onError={(e) => {
                    e.currentTarget.src = `data:image/svg+xml;base64,${btoa(`<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40"><rect width="40" height="40" fill="#f0f0f0"/><text x="20" y="25" text-anchor="middle" fill="#666" font-size="24">🔴</text></svg>`)}`;
                  }}
                />
              </td>
              <td>
                <button
                  className="edit-button"
                  onClick={() => handleEdit(sprite)}
                >
                  ✏️ Edit
                </button>
                <button
                  className="delete-button"
                  onClick={() => handleDeleteSprite(sprite.id)}
                >
                  🗑️ Delete
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="admin-data-management">
      <div className="data-management-header">
        <h2>🗃️ Data Management</h2>
        <p>Manage Pokemon, Items, and Custom Sprites</p>
      </div>

      <div className="data-management-tabs">
        <button
          className={`data-tab-button ${activeTab === 'pokemon' ? 'active' : ''}`}
          onClick={() => setActiveTab('pokemon')}
        >
          🔴 Pokemon Entries
        </button>
        <button
          className={`data-tab-button ${activeTab === 'items' ? 'active' : ''}`}
          onClick={() => setActiveTab('items')}
        >
          📦 Item Entries
        </button>
        <button
          className={`data-tab-button ${activeTab === 'sprites' ? 'active' : ''}`}
          onClick={() => setActiveTab('sprites')}
        >
          🎨 Pokemon Sprites
        </button>
      </div>

      <div className="data-management-controls">
        <div className="search-controls">
          <input
            type="text"
            placeholder={`Search ${activeTab}...`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
      </div>

      {loading ? (
        <div className="loading-spinner">
          <div className="spinner"></div>
        </div>
      ) : (
        <>
          {activeTab === 'pokemon' && renderPokemonTable()}
          {activeTab === 'items' && renderItemsTable()}
          {activeTab === 'sprites' && renderSpritesTable()}

          {renderPaginationControls()}
        </>
      )}

      {/* Edit Modal */}
      {editingItem && (
        <div className="modal-overlay" onClick={() => setEditingItem(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Edit {activeTab === 'pokemon' ? 'Pokemon' : activeTab === 'items' ? 'Item' : 'Sprite'}</h3>
              <button onClick={() => setEditingItem(null)} className="close-button">×</button>
            </div>
            <div className="modal-content">
              {activeTab === 'pokemon' ? renderPokemonEditForm() : renderGenericEditForm()}
            </div>
            <div className="modal-actions">
              <button onClick={handleSaveEdit} className="save-button">
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Sprite Modal */}
      {showCreateSprite && (
        <div className="modal-overlay" onClick={() => setShowCreateSprite(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Add New Pokemon Sprite</h3>
              <button onClick={() => setShowCreateSprite(false)} className="close-button">×</button>
            </div>
            <div className="modal-content">
              <div className="edit-form">
                <div className="form-group">
                  <label>Pokemon ID</label>
                  <input
                    type="number"
                    value={newSpriteData.pokemon_id}
                    onChange={(e) => setNewSpriteData({
                      ...newSpriteData,
                      pokemon_id: e.target.value
                    })}
                  />
                </div>
                <div className="form-group">
                  <label>Pokemon Name</label>
                  <input
                    type="text"
                    value={newSpriteData.pokemon_name}
                    onChange={(e) => setNewSpriteData({
                      ...newSpriteData,
                      pokemon_name: e.target.value
                    })}
                  />
                </div>
                <div className="form-group">
                  <label>Variant</label>
                  <input
                    type="text"
                    placeholder="e.g., Normal, Shiny, Halloween 2025 Female"
                    value={newSpriteData.variant}
                    onChange={(e) => setNewSpriteData({
                      ...newSpriteData,
                      variant: e.target.value
                    })}
                  />
                </div>
                <div className="form-group">
                  <label>Imgur URL</label>
                  <input
                    type="text"
                    placeholder="https://imgur.com/... or https://i.imgur.com/..."
                    value={newSpriteData.imgur_url}
                    onChange={(e) => setNewSpriteData({
                      ...newSpriteData,
                      imgur_url: e.target.value
                    })}
                  />
                </div>
                {newSpriteData.imgur_url && (
                  <div className="form-group">
                    <label>Preview</label>
                    <img
                      src={newSpriteData.imgur_url.includes('i.imgur.com')
                        ? newSpriteData.imgur_url
                        : `https://i.imgur.com/${newSpriteData.imgur_url.split('/').pop()}.jpg`}
                      alt="Preview"
                      className="sprite-preview-large"
                      onError={(e) => {
                        e.currentTarget.src = `data:image/svg+xml;base64,${btoa(`<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 100 100"><rect width="100" height="100" fill="#f0f0f0"/><text x="50" y="60" text-anchor="middle" fill="#666" font-size="48">❌</text></svg>`)}`;
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
            <div className="modal-actions">
              <button onClick={() => setShowCreateSprite(false)} className="cancel-button">
                Cancel
              </button>
              <button
                onClick={handleCreateSprite}
                className="save-button"
                disabled={!newSpriteData.pokemon_id || !newSpriteData.pokemon_name || !newSpriteData.variant || !newSpriteData.imgur_url}
              >
                Create Sprite
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDataManagement;
